import { useEffect, useMemo, useRef, type FocusEvent, type MouseEvent, type PointerEvent } from "react";
import { PRESS_SLOP_PX } from "../drag/gesture.ts";
import { parseTipAnchor, sameAnchor, type TipAnchor, type TipController } from "./tips.ts";

/**
 * The handlers a container of anchors takes.
 */
export interface TipHandlers {
  readonly onPointerOver: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerOut: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
  readonly onFocus: (event: FocusEvent<HTMLElement>) => void;
  readonly onBlur: (event: FocusEvent<HTMLElement>) => void;
  readonly onClick: (event: MouseEvent<HTMLElement>) => void;
  readonly onClickCapture: (event: MouseEvent<HTMLElement>) => void;
}

/**
 * The anchor element an event came from, and its anchor.
 *
 * @param target The event's target.
 * @returns The element and its anchor, or null when the event came from no anchor.
 */
export function anchorOf(target: EventTarget | null): { readonly element: Element; readonly anchor: TipAnchor } | null {
  const element = target instanceof Element ? target.closest("[data-tip]") : null;
  const anchor = element === null ? null : parseTipAnchor(element.getAttribute("data-tip"));
  return element === null || anchor === null ? null : { element, anchor };
}

/**
 * Whether a focus event's target is what the anchor shows its tooltip for: the anchor itself, or, for a wrapper of
 * words (`HoverWords`), the control inside it.
 *
 * @param found The anchor the event came from.
 * @param target The event's target.
 * @returns True when focus on the target shows or hides the anchor's tooltip.
 */
function wrapsFocus(found: { readonly element: Element; readonly anchor: TipAnchor }, target: Element): boolean {
  return found.element === target || (found.anchor.surface === "words" && found.element.contains(target));
}

/**
 * The handlers a container of anchors takes, made once per controller: a mouse or pen over an anchor hovers it, unless
 * a button is held, which is a drag passing over it (P2.8-T29), so no tooltip starts under a drag; a
 * primary press on one starts a long press, which a move past the slop or leaving the anchor stops short of firing,
 * and which ends only at the pointer's real release, on the anchor (`pressUp`) or anywhere else on the page, or the
 * browser's cancel (`pressRelease`, heard on the window), so a fired long press that wanders off and back still swallows
 * its click; keyboard focus (`:focus-visible`) shows it; a click on an effect icon pins or unpins it. A bar slot's own
 * click, which uses it, asks the controller first whether a long press swallowed it (`AbilityBar`), and a bag slot's own
 * click pins or unpins it itself, since a click on it may split or place instead (`bag/window.tsx`).
 *
 * @param tips The controller, or null where nothing shows tooltips.
 * @returns The handlers, or null.
 */
export function useTipHandlers(tips: TipController | null): TipHandlers | null {
  const pressed = useRef({ x: 0, y: 0, id: -1 });
  useEffect(() => {
    if (tips === null) {
      return undefined;
    }
    const released = (event: globalThis.PointerEvent): void => {
      if (pressed.current.id === event.pointerId) {
        pressed.current.id = -1;
        tips.pressRelease();
      }
    };
    window.addEventListener("pointerup", released);
    window.addEventListener("pointercancel", released);
    return () => {
      window.removeEventListener("pointerup", released);
      window.removeEventListener("pointercancel", released);
    };
  }, [tips]);
  return useMemo((): TipHandlers | null => {
    if (tips === null) {
      return null;
    }
    return {
      onPointerOver: (event) => {
        const found = anchorOf(event.target);
        const from = anchorOf(event.relatedTarget);
        if (found !== null && event.pointerType !== "touch" && event.buttons === 0 && !sameAnchor(from?.anchor ?? null, found.anchor)) {
          tips.hoverIn(found.anchor);
        }
      },
      onPointerOut: (event) => {
        const found = anchorOf(event.target);
        const to = anchorOf(event.relatedTarget);
        if (found === null || sameAnchor(to?.anchor ?? null, found.anchor)) {
          return;
        }
        tips.hoverOut(found.anchor);
        if (pressed.current.id === event.pointerId) {
          tips.pressDrift();
        }
      },
      onPointerDown: (event) => {
        const found = anchorOf(event.target);
        if (found !== null && event.button === 0) {
          pressed.current = { x: event.clientX, y: event.clientY, id: event.pointerId };
          tips.pressDown(found.anchor);
        }
      },
      onPointerMove: (event) => {
        const press = pressed.current;
        if (press.id === event.pointerId && Math.hypot(event.clientX - press.x, event.clientY - press.y) > PRESS_SLOP_PX) {
          tips.pressDrift();
        }
      },
      onPointerUp: (event) => {
        const found = anchorOf(event.target);
        if (pressed.current.id !== event.pointerId) {
          return;
        }
        pressed.current.id = -1;
        if (found === null) {
          tips.pressRelease();
        } else {
          tips.pressUp(found.anchor);
        }
      },
      onPointerCancel: (event) => {
        if (pressed.current.id === event.pointerId) {
          pressed.current.id = -1;
          tips.pressRelease();
        }
      },
      onFocus: (event) => {
        const found = anchorOf(event.target);
        if (found !== null && event.target instanceof Element && wrapsFocus(found, event.target) && event.target.matches(":focus-visible")) {
          tips.focusIn(found.anchor);
        }
      },
      onBlur: (event) => {
        const found = anchorOf(event.target);
        if (found !== null && event.target instanceof Element && wrapsFocus(found, event.target)) {
          tips.focusOut(found.anchor);
        }
      },
      onClickCapture: (event) => {
        const found = anchorOf(event.target);
        if (found !== null && found.anchor.surface === "words" && tips.swallowClick(found.anchor)) {
          event.stopPropagation();
          event.preventDefault();
        }
      },
      onClick: (event) => {
        const found = anchorOf(event.target);
        if (found !== null && found.anchor.surface !== "slot" && found.anchor.surface !== "bag" && (found.anchor.surface !== "words" || found.element.hasAttribute("data-tip-pin"))) {
          tips.click(found.anchor);
        }
      },
    };
  }, [tips]);
}
